import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";

import { ISyncable } from "./ISyncable";
import { ErrorPageCode } from "../../app.enums";
import { ApplicationFactory } from "../../factories/ApplicationFactory";

import {
  GenericErrorHandler,
  ActivityIndicatorService,
  UserRoleEnum,
  ApplicationTypeEnum,
  IUserDetails,
  CLAIM_NAME,
  CLAIM_PICTURE,
  CLAIM_EMAIL,
  AppConfigService,
  PublicParam,
  DeviceService,
} from "../../../core-ui";
import { UserService } from "../../../core-ui/services/user.service";
import { AuthenticationService } from "../../../core-ui/services/authentication.service";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { LocaleService } from "../../services";

@Injectable()
export class AuthAgentGuard implements ISyncable {
  constructor(
    private router: Router,
    private errorHandler: GenericErrorHandler,
    private activity: ActivityIndicatorService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private applicationService: ApplicationService,
    private applicationFactory: ApplicationFactory,
    private config: AppConfigService,
    private device: DeviceService,
    private locale: LocaleService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (!this.userService.isAuthenticated()) {
      try {
        const message =
          this.config.publicParam(PublicParam.POPUP_AUTHENTICATION_ENABLED) &&
          !this.device.isPopUp
            ? "Authenticating... Please allow popups"
            : ""; // "authenticating"

        this.activity.loading(true, message, true);

        const user = await this.authenticationService.authenticate(route);

        if (!user.hasRole(UserRoleEnum.agent)) {
          // TODO: this message is displayed on the ui
          throw new Error("user is not an agent");
        }

        this.userService.setActiveUser(user);

        // refresh application now that we have a jwt token
        this.applicationService.createApiResource();
        const application =
          await this.applicationFactory.createAgentApplication(
            this.applicationService.getActiveApplication().getId(),
            route
          );
        await this.applicationService.setActiveApplication(application);

        // todo: do it better. on genesys it is set on GenesysCloudApplication / afterAuth.
        if (
          [
            ApplicationTypeEnum.StandaloneOpenID,
            ApplicationTypeEnum.TalkdeskOpenID,
            ApplicationTypeEnum.NiceOpenID,
          ].includes(application.getType())
        ) {
          const details: IUserDetails = {
            id: user.getId(),
            name: user.getClaim(CLAIM_NAME),
            avatarUrl: user.getClaim(CLAIM_PICTURE),
            email: user.getClaim(CLAIM_EMAIL),
          };
          this.userService.setUserDetails(details);
        }
      } catch (error) {
        this.errorHandler.handleError(error);
        let message = error.message;
        if (error.status === 401) {
          message = "Please reload the page to try again. " + error.message;
        }
        return this.router.createUrlTree(
          ["/error", ErrorPageCode.AUTHENTICATION_FAILURE],
          {
            queryParams: { message },
            queryParamsHandling: "merge",
          }
        );
      } finally {
        this.activity.loading(false);
      }
    }
     // try fix for race condition where sometimes the locale.guard was not authenticated
    // await this.loadCustomTranslations();
    return true;
  }

  private async loadCustomTranslations() {
    try {
      await this.locale.loadInstanceTranslationsForLocale();
    } catch (ex) {
      // do nothing. it uses the default ones
    }
  }
}
